import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, MapPin, MessageCircle, Plus, Tag } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { CountUp } from "@/components/common/CountUp";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORY_LABELS, useAssets, useBlobUrl } from "@/hooks/use-assets";
import { MarketMap } from "@/components/market/MarketMap";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";

const CONDITIONS = { neuf: "Neuf", tres_bon: "Très bon état", bon: "Bon état", correct: "État correct" };
const STATUS = { active: ["En ligne", "badge-saving"], reserved: ["Réservé", "badge-decision"], sold: ["Vendu", "rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-bold"] };
const TONES = { appliance: "tone-sky", computer: "tone-lilac", multimedia: "tone-rose", furniture: "tone-butter", tools: "tone-peach", vehicle: "tone-mint", jewelry: "tone-butter", art: "tone-rose", sport: "tone-mint", other: "tone-sky" };

export const useMarket = (hid) => useQuery({ queryKey: ["market", hid], queryFn: () => api.get(`/market/${hid}`).then((r) => r.data), enabled: !!hid });

export const MarketStats = ({ s }) => {
  const { money } = useFormat();
  const cells = [["Annonces en ligne", s.active, (v) => Math.round(v)], ["Vues", s.views, (v) => Math.round(v)], ["Messages", s.messages, (v) => Math.round(v)], ["Déjà vendu", s.sold_total, (v) => money(v, { maximumFractionDigits: 0 })]];
  return (
    <div className="stagger grid grid-cols-2 gap-3 lg:grid-cols-4">{cells.map(([l, v, f], i) => (
      <div key={l} className={cn("card-surface lift p-4", i === 3 && "saving-strong")} data-testid={`market-stat-${i}`}><div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{l}</div><div className="num mt-1 text-2xl font-extrabold"><CountUp value={v} format={f} /></div></div>))}</div>
  );
};

const Photo = ({ lid, fid, idx, active, onClick }) => {
  const url = useBlobUrl(`/market/photo/${lid}/${fid}`);
  return <button type="button" onClick={onClick} aria-label={`Photo ${idx + 1}`} data-testid={`market-photo-thumb-${lid}-${idx}`} className={cn("h-10 w-10 shrink-0 overflow-hidden rounded-lg border-2", active ? "border-primary" : "border-transparent")}>{url && <img src={url} alt="" className="h-full w-full object-cover" />}</button>;
};

const Thumb = ({ l }) => {
  const [i, setI] = useState(0);
  const photos = l.photos || [];
  const url = useBlobUrl(photos.length ? `/market/photo/${l.id}/${photos[i]}` : null);
  if (!photos.length) return <div className={cn("tone-tile flex h-40 items-center justify-center rounded-xl", TONES[l.category])}><Tag className="h-10 w-10 opacity-60" /></div>;
  return (
    <div className="space-y-1.5" data-testid={`market-photos-${l.id}`}>
      <div className="h-40 overflow-hidden rounded-xl bg-muted">{url && <img src={url} alt={l.title} className="h-full w-full object-cover" data-testid={`market-photo-main-${l.id}`} />}</div>
      {photos.length > 1 && <div className="flex gap-1.5 overflow-x-auto">{photos.map((f, j) => <Photo key={f} lid={l.id} fid={f} idx={j} active={j === i} onClick={() => setI(j)} />)}</div>}
    </div>
  );
};

const MyListing = ({ l, hid, onChange }) => {
  const { money } = useFormat();
  const put = async (status) => { try { await api.put(`/market/${hid}/listings/${l.id}`, { status }); onChange(); } catch (e) { toast.error(formatApiError(e)); } };
  const del = async () => { try { await api.delete(`/market/${hid}/listings/${l.id}`); onChange(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <li className="card-surface lift flex flex-col gap-2 p-4" data-testid={`market-mine-${l.id}`}>
      <Thumb l={l} />
      <div className="flex items-start justify-between gap-2"><b className="text-sm">{l.title}</b><span className={STATUS[l.status][1]}>{STATUS[l.status][0]}</span></div>
      <div className="num text-xl font-extrabold">{money(l.price, { maximumFractionDigits: 0 })}</div>
      <div className="flex gap-3 text-xs text-muted-foreground"><span className="flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{l.views}</span><span className="flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" />{l.messages}</span><span>{CONDITIONS[l.condition]}</span></div>
      <div className="mt-auto flex items-center gap-2">
        <Select value={l.status} onValueChange={put}><SelectTrigger className="h-8 rounded-full text-xs" data-testid={`market-status-${l.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(STATUS).map(([k, [lab]]) => <SelectItem key={k} value={k}>{lab}</SelectItem>)}</SelectContent></Select>
        <ConfirmDelete title="Supprimer l'annonce ?" text={l.title} testid={`market-delete-${l.id}`} onConfirm={del} />
      </div>
    </li>
  );
};

const Nearby = ({ l, hid, onChange }) => {
  const { money } = useFormat();
  const contact = async () => { try { await api.post(`/market/${hid}/contact/${l.id}`); toast.success(`Message envoyé à ${l.seller || "l'annonceur"}`); onChange(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <li className="card-surface lift flex flex-col gap-2 p-4" data-testid={`market-nearby-${l.id}`}>
      <Thumb l={l} />
      <b className="text-sm">{l.title}</b>
      <div className="num text-xl font-extrabold">{money(l.price, { maximumFractionDigits: 0 })}</div>
      <div className="text-xs text-muted-foreground">{CONDITIONS[l.condition]} · {CATEGORY_LABELS[l.category]}</div>
      {l.city && <div className="flex items-center gap-1 text-xs"><MapPin className="h-3.5 w-3.5" />{l.city} · {l.seller}{l.distance_km != null && <b className="ml-auto" data-testid={`market-distance-${l.id}`}>{String(l.distance_km).replace(".", ",")} km</b>}</div>}
      {l.description && <p className="line-clamp-2 text-xs text-muted-foreground">{l.description}</p>}
      <Button size="sm" className="mt-auto rounded-full" onClick={contact} data-testid={`market-contact-${l.id}`}><MessageCircle className="mr-1 h-3.5 w-3.5" /> Contacter</Button>
    </li>
  );
};

const SellForm = ({ hid, onDone }) => {
  const { data: assets = [] } = useAssets();
  const [v, setV] = useState({ title: "", category: "other", condition: "bon", price: "", asset_id: "none" });
  const pick = (id) => { const a = assets.find((x) => x.id === id); setV({ ...v, asset_id: id, ...(a ? { title: a.name, category: a.category || "other" } : {}) }); };
  const picked = assets.find((x) => x.id === v.asset_id);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/market/${hid}/listings`, { ...v, price: Number(v.price), asset_id: v.asset_id === "none" ? null : v.asset_id }); toast.success("Annonce publiée"); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="decision-strong grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-[12rem_1fr_10rem_9rem_7rem_auto]" data-testid="market-sell-form">
      <Select value={v.asset_id} onValueChange={pick}><SelectTrigger data-testid="market-sell-asset"><SelectValue placeholder="Depuis l'inventaire" /></SelectTrigger>
        <SelectContent><SelectItem value="none">Hors inventaire</SelectItem>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
      <Input value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} placeholder="Titre de l'annonce" required minLength={3} data-testid="market-sell-title" />
      <Select value={v.category} onValueChange={(c) => setV({ ...v, category: c })}><SelectTrigger data-testid="market-sell-category"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
      <Select value={v.condition} onValueChange={(c) => setV({ ...v, condition: c })}><SelectTrigger data-testid="market-sell-condition"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(CONDITIONS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
      <Input type="number" min="0" value={v.price} onChange={(e) => setV({ ...v, price: e.target.value })} placeholder="Prix €" required data-testid="market-sell-price" />
      <Button type="submit" className="rounded-full" data-testid="market-sell-submit">Publier</Button>
      {picked && <p className="text-xs text-muted-foreground sm:col-span-2 lg:col-span-6" data-testid="market-sell-photos-info">{(picked.photo_ids || []).length ? `${picked.photo_ids.length} photo(s) de la fiche inventaire seront ajoutées à l'annonce (photo principale en premier).` : "Cette fiche n'a pas de photo : ajoutez-en depuis l'inventaire pour illustrer l'annonce."}</p>}
    </form>
  );
};

export const MarketBoard = ({ hid }) => {
  const qc = useQueryClient();
  const [selling, setSelling] = useState(false);
  const { data } = useMarket(hid);
  if (!data) return <div className="h-64 animate-pulse rounded-2xl bg-muted" />;
  const refresh = () => qc.invalidateQueries({ queryKey: ["market"] });
  return (
    <div className="space-y-8">
      <MarketStats s={data.stats} />
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2"><h2 className="text-xl">Mes annonces</h2>
          <Button className="rounded-full" onClick={() => setSelling(!selling)} data-testid="market-sell-open"><Plus className="mr-1 h-4 w-4" /> Vendre un bien</Button></div>
        {selling && <SellForm hid={hid} onDone={() => { setSelling(false); refresh(); }} />}
        {data.mine.length ? <ul className="stagger grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="market-mine-list">{data.mine.map((l) => <MyListing key={l.id} l={l} hid={hid} onChange={refresh} />)}</ul>
          : <p className="text-sm text-muted-foreground">Aucune annonce. Vendez un bien de votre inventaire en un clic.</p>}
      </section>
      <section className="space-y-3">
        <h2 className="text-xl">Près de chez vous {data.is_demo && <Badge variant="outline" className="ml-2 rounded-full align-middle">annonces fictives</Badge>}</h2>
        <MarketMap home={data.home} items={data.nearby} />
        {data.nearby.length ? <ul className="stagger grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="market-nearby-list">{data.nearby.map((l) => <Nearby key={l.id} l={l} hid={hid} onChange={refresh} />)}</ul>
          : <p className="text-sm text-muted-foreground">Aucune annonce pour le moment.</p>}
      </section>
    </div>
  );
};
